import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Clock, Plus, Trash2 } from "../../../components/icons";

interface RecurringSlot {
  id: string;
  weekday: number;
  startTime: string;
  endTime: string;
}

interface RecurrenceSectionProps {
  t: any;
  recurringSlots: RecurringSlot[];
  addSlot: () => void;
  removeSlot: (id: string) => void;
  updateSlot: (id: string, field: keyof RecurringSlot, value: any) => void;
  endType: "never" | "weeks";
  setEndType: (type: "never" | "weeks") => void;
  recurrenceWeeks: number;
  setRecurrenceWeeks: (weeks: number) => void;
}

export function RecurrenceSection({
  t,
  recurringSlots,
  addSlot,
  removeSlot,
  updateSlot,
  endType,
  setEndType,
  recurrenceWeeks,
  setRecurrenceWeeks,
}: RecurrenceSectionProps) {
  return (
    <section className="space-y-6 pt-8 border-t">
      <div className="space-y-4">
        <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.createDrawer.frequency}</Label>
        <div className="space-y-4">
          {recurringSlots.map((slot, index) => (
            <div key={slot.id} className="p-4 border rounded-xl bg-white shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-neutral-400 uppercase">Horário {index + 1}</span>
                {recurringSlots.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <Select
                  value={String(slot.weekday)}
                  onValueChange={(v) => updateSlot(slot.id, "weekday", Number(v))}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {t.createDrawer.weekdays.map((day: string, i: number) => (
                      <SelectItem key={i} value={String(i)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <Input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) => updateSlot(slot.id, "startTime", e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <Input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) => updateSlot(slot.id, "endTime", e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addSlot}
            className="w-full border-dashed border-2 h-12 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t.createDrawer.addSlot}
          </Button>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <div className="space-y-2">
          <Label className="text-xs text-neutral-500 font-medium uppercase tracking-wider">{t.createDrawer.endType}</Label>
          <Select value={endType} onValueChange={(v) => setEndType(v as "never" | "weeks")}>
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">{t.createDrawer.endTypeOptions.never}</SelectItem>
              <SelectItem value="weeks">{t.createDrawer.endTypeOptions.weeks}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {endType === "weeks" && (
          <div className="space-y-2">
            <Label className="text-xs text-neutral-500 font-medium uppercase tracking-wider">
              {t.createDrawer.weeksLabel}
            </Label>
            <Input
              type="number"
              min="1"
              max="52"
              value={recurrenceWeeks}
              onChange={(e) => setRecurrenceWeeks(Number(e.target.value))}
              className="h-10"
            />
          </div>
        )}
      </div>
    </section>
  );
}
